import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash } from 'react-icons/fa';
import './CallLobby.css';

const CallLobby = ({ meeting, onJoin, onCancel }) => {
    const isVoiceOnly = meeting?.call_type === 'voice';
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoOff, setIsVideoOff] = useState(isVoiceOnly);
    const [stream, setStream] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [audioLevel, setAudioLevel] = useState(0);
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const analyserRef = useRef(null);
    const animFrameRef = useRef(null);
    const audioCtxRef = useRef(null);
    const micBtnRef = useRef(null);

    useEffect(() => {
        let isMounted = true;
        let currentStream = null;

        const startPreview = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: !isVoiceOnly });
                
                if (!isMounted) {
                    stream.getTracks().forEach(track => track.stop());
                    return;
                }
                
                currentStream = stream;
                streamRef.current = currentStream;
                setStream(currentStream);
                if (videoRef.current) {
                    videoRef.current.srcObject = currentStream;
                }

                // Set up audio level analyser for speaking indicator
                const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                audioCtxRef.current = audioCtx;
                if (audioCtx.state === 'suspended') audioCtx.resume();
                const source = audioCtx.createMediaStreamSource(currentStream);
                const analyser = audioCtx.createAnalyser();
                analyser.fftSize = 256;
                source.connect(analyser);
                analyserRef.current = analyser;

                const dataArray = new Uint8Array(analyser.frequencyBinCount);
                const detectSpeaking = () => {
                    analyser.getByteFrequencyData(dataArray);
                    const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
                    const level = Math.min(avg / 60, 1); // normalize 0-1
                    setIsSpeaking(avg > 8);
                    setAudioLevel(level);
                    animFrameRef.current = requestAnimationFrame(detectSpeaking);
                };
                detectSpeaking();
            } catch (err) {
                console.error("Error accessing media devices for preview:", err);
            }
        };

        startPreview();

        return () => {
            isMounted = false;
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
            if (currentStream) currentStream.getTracks().forEach(track => track.stop());
            if (audioCtxRef.current) audioCtxRef.current.close();
        };
    }, []);

    // Re-attach stream to video element whenever it re-mounts (camera toggled back on)
    const attachStream = useCallback((node) => {
        videoRef.current = node;
        if (node && streamRef.current) {
            node.srcObject = streamRef.current;
        }
    }, []);

    useEffect(() => {
        if (stream) {
            const videoTrack = stream.getVideoTracks()[0];
            if (videoTrack) videoTrack.enabled = !isVideoOff;
            const audioTrack = stream.getAudioTracks()[0];
            if (audioTrack) audioTrack.enabled = !isMuted;
        }
    }, [isVideoOff, isMuted, stream]);

    const handleJoin = () => {
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        if (stream) stream.getTracks().forEach(track => track.stop());
        onJoin({ isMuted, isVideoOff });
    };

    return (
        <div className="call-lobby">
            <div className="lobby-content">
                <div className="preview-container">
                    {isVoiceOnly ? (
                        <div className="video-off-placeholder">
                            <div className="avatar">
                                {meeting?.title?.charAt(0) || '?'}
                            </div>
                            <p>Voice Call</p>
                        </div>
                    ) : isVideoOff ? (
                        <div className="video-off-placeholder">
                            <div className="avatar">
                                {meeting?.title?.charAt(0) || '?'}
                            </div>
                            <p>Camera is off</p>
                        </div>
                    ) : (
                        <video ref={attachStream} autoPlay muted playsInline className="preview-video" />
                    )}

                    <div className="preview-controls">
                        <button
                            ref={micBtnRef}
                            className={`lobby-control-btn ${isMuted ? 'muted' : ''} ${!isMuted && isSpeaking ? 'speaking' : ''}`}
                            style={!isMuted ? { '--mic-level': audioLevel } : undefined}
                            onClick={() => setIsMuted(!isMuted)}
                            title={isMuted ? "Unmute" : "Mute"}
                        >
                            {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
                        </button>
                        {!isVoiceOnly && (
                            <button
                                className={`lobby-control-btn ${isVideoOff ? 'muted' : ''}`}
                                onClick={() => setIsVideoOff(!isVideoOff)}
                                title={isVideoOff ? "Turn Camera On" : "Turn Camera Off"}
                            >
                                {isVideoOff ? <FaVideoSlash /> : <FaVideo />}
                            </button>
                        )}
                    </div>
                </div>

                <div className="lobby-info">
                    <h1>{meeting?.title || 'Meeting'}</h1>
                    <p>Ready to join?</p>

                    <div className="lobby-actions">
                        <button className="join-now-btn" onClick={handleJoin}>
                            Join Now
                        </button>
                        <button className="cancel-btn" onClick={onCancel}>
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CallLobby;
